import { useEffect, useState } from 'react';

import {
  agentPromptWarningConsequence,
  describeAgentPromptWarning,
  diagnoseAgentPromptOverrides,
  type AgentPromptWarning,
} from '@sb/feathers-types';
import {
  getAgentSettings,
  subscribeAgentSettingsChanged,
} from '@sbrc/services/feathers-client/agent-settings';

/**
 * The prompt-override warnings agent-bot would log for the current settings,
 * judged client-side by the same shared function: read once when `enabled`
 * turns true (a panel opening), then kept current from the settings the
 * service publishes after every save. A failed read yields no warnings: this
 * is advisory, never a gate.
 */
export function useAgentPromptWarnings(enabled = true): AgentPromptWarning[] {
  const [warnings, setWarnings] = useState<AgentPromptWarning[]>([]);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;

    const judge = (overrides: {
      systemPrompt?: string | null;
      sessionContextPrompt?: string | null;
    }): void => {
      if (cancelled) return;

      setWarnings(
        diagnoseAgentPromptOverrides({
          systemPrompt: overrides.systemPrompt ?? null,
          sessionContextPrompt: overrides.sessionContextPrompt ?? null,
        }),
      );
    };

    getAgentSettings()
      .then(judge)
      .catch(() => {
        if (!cancelled) setWarnings([]);
      });

    const unsubscribe = subscribeAgentSettingsChanged(judge);

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [enabled]);

  return warnings;
}

interface AgentPromptWarningsProps {
  warnings: AgentPromptWarning[];
  testId: string;
  className?: string;
}

/** Renders nothing when there is nothing to warn about. */
export function AgentPromptWarnings({
  warnings,
  testId,
  className,
}: AgentPromptWarningsProps) {
  if (warnings.length === 0) return null;

  return (
    <div role="status" data-testid={testId} className={className}>
      {warnings.map((warning) => (
        <p
          key={`${warning.template}:${warning.kind}`}
          className="tw-text-13 tw-text-orange tw-mb-4"
        >
          {describeAgentPromptWarning(warning)}{' '}
          {agentPromptWarningConsequence(warning)}
        </p>
      ))}
    </div>
  );
}
